import React, { useState } from 'react';
import { Minus, Plus } from 'lucide-react';

interface JsonTreeViewerProps {
  data: unknown;
  defaultExpanded?: boolean;
  level?: number;
  path?: string;
  expandedPaths?: Set<string>;
  onTogglePath?: (path: string) => void;
}

const JsonTreeViewer: React.FC<JsonTreeViewerProps> = ({
  data,
  defaultExpanded = false,
  level = 0,
  path = 'root',
  expandedPaths: externalExpandedPaths,
  onTogglePath: externalOnTogglePath,
}) => {
  const [internalExpandedPaths, setInternalExpandedPaths] = useState<
    Set<string>
  >(() => {
    if (defaultExpanded && path === 'root') {
      return new Set([path]);
    }
    return new Set();
  });

  const expandedPaths = externalExpandedPaths ?? internalExpandedPaths;
  const onTogglePath =
    externalOnTogglePath ??
    ((p: string) => {
      setInternalExpandedPaths((prev) => {
        const next = new Set(prev);
        if (next.has(p)) {
          next.delete(p);
        } else {
          next.add(p);
        }
        return next;
      });
    });

  const isExpanded = expandedPaths.has(path);

  const toggleExpand = () => {
    onTogglePath(path);
  };

  // Handle primitive values
  if (data === null) {
    return <span className="text-chart-7 font-mono text-sm">null</span>;
  }

  if (typeof data === 'boolean') {
    return (
      <span className="text-primary font-mono text-sm">
        {data ? 'true' : 'false'}
      </span>
    );
  }

  if (typeof data === 'number') {
    return <span className="text-green-600 font-mono text-sm">{data}</span>;
  }

  if (typeof data === 'string') {
    return (
      <span className="text-destructive font-mono text-sm">
        &quot;{data}&quot;
      </span>
    );
  }

  // Handle arrays
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span className="text-gray-600 font-mono text-sm">[]</span>;
    }

    return (
      <div className="relative">
        <button
          onClick={toggleExpand}
          className="flex items-center gap-1.5 group focus:outline-none focus:ring-0 rounded px-1 -ml-1 transition-colors hover:bg-gray-50"
          aria-label={isExpanded ? 'Collapse array' : 'Expand array'}
          aria-expanded={isExpanded}
        >
          <span className="text-gray-400 group-hover:text-gray-600 transition-colors">
            {isExpanded ? (
              <Minus className="w-3.5 h-3.5" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
          </span>
          <span className="text-gray-600 font-mono text-sm">[</span>
          {!isExpanded && (
            <span className="text-gray-500 text-xs">
              {data.length} item{data.length !== 1 ? 's' : ''}
            </span>
          )}
        </button>
        {isExpanded && (
          <div className="ml-4 mt-1 relative">
            {/* Vertical line */}
            {data.length > 0 && (
              <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-300" />
            )}
            {data.map((item, index) => {
              const isLast = index === data.length - 1;
              const itemPath = `${path}[${index}]`;
              return (
                <div key={index} className="relative pl-4 pb-0.5">
                  {/* Horizontal line */}
                  <div className="absolute left-0 top-2.5 w-3 h-px bg-gray-300" />
                  {/* Vertical line connector */}
                  {!isLast && (
                    <div className="absolute left-0 top-2.5 bottom-0 w-px bg-gray-300" />
                  )}
                  <div className="flex items-start gap-2 min-h-[20px]">
                    <span className="text-gray-500 font-mono text-xs flex-shrink-0 pt-0.5">
                      {index}:
                    </span>
                    <div className="flex-1 min-w-0">
                      <JsonTreeViewer
                        data={item}
                        level={level + 1}
                        path={itemPath}
                        expandedPaths={expandedPaths}
                        onTogglePath={onTogglePath}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="text-gray-600 font-mono text-sm mt-0.5">]</div>
          </div>
        )}
      </div>
    );
  }

  // Handle objects
  if (typeof data === 'object') {
    const keys = Object.keys(data as Record<string, unknown>);
    if (keys.length === 0) {
      return <span className="text-gray-600 font-mono text-sm">{'{}'}</span>;
    }

    return (
      <div className="relative">
        <button
          onClick={toggleExpand}
          className="flex items-center gap-1.5 group focus:outline-none focus:0 rounded px-1 -ml-1 transition-colors hover:bg-gray-50"
          aria-label={isExpanded ? 'Collapse object' : 'Expand object'}
          aria-expanded={isExpanded}
        >
          <span className="text-gray-400 group-hover:text-gray-600 transition-colors">
            {isExpanded ? (
              <Minus className="w-3.5 h-3.5" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
          </span>
          <span className="text-gray-600 font-mono text-sm">{'{'}</span>
          {!isExpanded && (
            <span className="text-gray-500 text-xs">
              {keys.length} key{keys.length !== 1 ? 's' : ''}
            </span>
          )}
        </button>
        {isExpanded && (
          <div className="ml-4 mt-1 relative">
            {/* Vertical line */}
            {keys.length > 0 && (
              <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-300" />
            )}
            {keys.map((key, index) => {
              const isLast = index === keys.length - 1;
              const itemPath = `${path}.${key}`;
              const value = (data as Record<string, unknown>)[key];

              return (
                <div key={key} className="relative pl-4 pb-0.5">
                  {/* Horizontal line */}
                  <div className="absolute left-0 top-2.5 w-3 h-px bg-gray-300" />
                  {/* Vertical line connector */}
                  {!isLast && (
                    <div className="absolute left-0 top-2.5 bottom-0 w-px bg-gray-300" />
                  )}
                  <div className="flex items-start gap-2 min-h-[20px]">
                    <span className="text-gray-700 font-mono text-sm flex-shrink-0 pt-0.5">
                      &quot;{key}&quot;:
                    </span>
                    <div className="flex-1 min-w-0">
                      <JsonTreeViewer
                        data={value}
                        level={level + 1}
                        path={itemPath}
                        expandedPaths={expandedPaths}
                        onTogglePath={onTogglePath}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="text-gray-600 font-mono text-sm mt-0.5">{'}'}</div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default JsonTreeViewer;
